import {
  ActionIcon,
  CustomIcon,
  DeviceIcon,
  FavouriteIcon,
} from '@tymbe/ui-icons';
import { ShiftData } from '../../../../types/TymbeApi';
import { Gender } from '@tymbe/schema/enums';
import { useQuery } from 'react-query';
import apiClient from '../../../../apiClient';
import { calculateWorkTime } from '../../../../utils/time';
import { ApplicationState } from '../../../../utils/enums';
import { isDeleted, notDeleted } from '../utils';

interface AttendanceStatsProps {
  shift: ShiftData;
}

const AttendanceStats = ({ shift }: AttendanceStatsProps) => {
  const { data: shiftWithCounts } = useQuery('shiftWithCounts', async () =>
    apiClient.service('shift').get(shift.id, {
      query: {
        $modify: 'withApplicationCounts',
      },
    })
  );

  const approvedTimeInMinutes =
    shift.application
      ?.filter(notDeleted)
      ?.filter((app) => app.attendance && notDeleted(app.attendance))
      .flatMap((app) => [
        Number(app.attendance?.confirmed_time) || 0,
        Number(app.attendance?.confirmed_overtime) || 0,
        Number(app.attendance?.confirmed_credit_time) || 0,
      ])
      .reduce((accumulator, currentValue) => {
        return accumulator + currentValue;
      }, 0) || 0;

  const notApprovedTimeInHours =
    (shift.application
      ?.filter(notDeleted)
      ?.filter((app) => !app.attendance || isDeleted(app.attendance))
      .length || 0) *
    calculateWorkTime(shift.start_time, shift.end_time, false, 'hour');

  return (
    <div className="flex flex-col p-4 bg-secondary-transparent-5 rounded-3xl text-caption-md text-secondary-400 lg:flex-row lg:justify-between">
      <div className='flex flex-col'>
        <div>Tymbeři</div>
        <div className="flex w-full justify-between mb-4 lg:mb-0 lg:mt-auto">
          <div className="flex gap-1.5">
            <ActionIcon name="supervisor_account" className="size-4" />
            <span className="text-secondary-600">
              {shiftWithCounts?.filled_orders_count}
            </span>
            Celkem
          </div>
          <div className="flex gap-1.5">
            <FavouriteIcon name="female" className="size-4" />
            <span className="text-secondary-600">
              {shift.application?.filter(notDeleted)?.filter(
                (app) => app.person?.personData?.gender === Gender.FEMALE
              ).length || '-'}
            </span>
            Ženy
          </div>
          <div className="flex gap-1.5">
            <FavouriteIcon name="male" className="size-4" />
            <span className="text-secondary-600">
              {shift.application?.filter(notDeleted)?.filter(
                (app) => app.person?.personData?.gender === Gender.MALE
              ).length || '-'}
            </span>
            Muži
          </div>
          <div className="flex gap-1.5">
            <CustomIcon name="rookie" className="size-4" />
            <span className="text-secondary-600">
              {shift.manShift?.filter(
                (manShift) =>
                  manShift?.activeApplication?.isCompanyJunior &&
                  manShift.activeApplication.state ===
                    ApplicationState.CONFIRMED
              ).length || '-'}
            </span>
            Nováčci
          </div>
        </div>
      </div>

      <div className="flex justify-between lg:gap-4">
        <div className="flex flex-col gap-1.5">
          Docházka k potvrzení
          <div className="flex gap-4">
            <div className="flex gap-1.5">
              <DeviceIcon name="access_time_filled" className="size-4" />
              <span className="text-secondary-600">
                {Number(notApprovedTimeInHours).toFixed(2).replace('.', ',')}
              </span>
              h
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          Potvrzená docházka
          <div className="flex gap-4">
            <div className="flex gap-1.5">
              <DeviceIcon name="access_time_filled" className="size-4" />
              <span className="text-secondary-600">
                {Number(approvedTimeInMinutes / 60)
                  .toFixed(2)
                  .replace('.', ',')}
              </span>
              h
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceStats;
